/**
 * "Write a draft" beside a connector's Guide field (item 65B, 2026-10-30).
 *
 * Asks the server for a first draft of the guide, written from the
 * connector's TOOL DEFINITIONS only (the tools left on: names, descriptions,
 * parameters) in the language the person is using — never a credential,
 * never anything a tool returned (guide_draft_service whitelists the
 * request). Nothing is saved: an empty Guide takes the draft to edit; a
 * Guide that already has text shows the draft beside it, to use or discard.
 */

import { useRef, useState } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { connectorLibraryService } from '@modules/connector_library/services/connectorLibraryService';
import { phraseConnectorError } from './ConnectorDialog';

export interface DraftTool {
  name: string;
  description?: string;
  parameters?: { name: string; type?: string | null; required?: boolean; description?: string }[];
}

/** Where the connector is edited — the server drafts only for someone who may
 *  edit it there: an agent they may edit, or a library entry of an
 *  organization they manage (the server then adds each tool's stored parameters). */
export type DraftScope = { chatbotId: string; connectorId?: string } | { entryId: string; tenantId: string };

interface Props {
  kind: 'mcp' | 'api';
  name: string;
  description?: string;
  tools: DraftTool[];
  scope: DraftScope;
  /** What the Guide holds now. */
  current: string;
  onUse: (text: string) => void;
  disabled?: boolean;
  /** Why there is nothing to draft from yet (e.g. "connect it first"). */
  emptyHint?: string;
  testId: string;
}

export function GuideDraft({ kind, name, description, tools, scope, current, onUse, disabled, emptyHint, testId }: Props) {
  const { t, language } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ model: string; cost: number; cutOff: boolean } | null>(null);
  // Read when the draft arrives, not when it was asked for: text typed
  // meanwhile is never replaced without asking.
  const currentRef = useRef(current);
  currentRef.current = current;

  const ask = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await connectorLibraryService.draftGuide({
        kind, name, description, language,
        tools: tools.map(tl => ({ name: tl.name, description: tl.description, parameters: tl.parameters })),
        ...('chatbotId' in scope
          ? { chatbot_id: scope.chatbotId, ...(scope.connectorId ? { connector_id: scope.connectorId } : {}) }
          : { entry_id: scope.entryId, tenant_id: scope.tenantId }),
      });
      setMeta({ model: result.model, cost: result.cost_usd, cutOff: result.cut_off });
      if (currentRef.current.trim()) setPending(result.draft); else onUse(result.draft);
    } catch (err) {
      setError(phraseConnectorError(t, err, 'bots.connectors.guideDraft.error'));
    } finally {
      setBusy(false);
    }
  };

  const nothing = tools.length === 0;
  return (
    <div className="cxg" data-testid={testId}>
      <div className="cxg__bar">
        <button type="button" className="cx-btn cx-btn--sm" onClick={() => { void ask(); }}
                disabled={disabled || busy || nothing} data-testid={`${testId}-button`}>
          <SparkGlyph />
          <span>{busy ? t('bots.connectors.guideDraft.writing') : t('bots.connectors.guideDraft.button')}</span>
        </button>
        <span className="cxg__hint">
          {nothing ? (emptyHint ?? t('bots.connectors.guideDraft.noTools')) : t('bots.connectors.guideDraft.hint')}
        </span>
      </div>
      {error && <p className="cxg__error" role="alert">{error}</p>}
      {pending !== null && (
        <div className="cxg__pending" data-testid={`${testId}-pending`}>
          <p className="cxg__pending-title">{t('bots.connectors.guideDraft.pendingTitle')}</p>
          <div className="cxg__pending-text" dir="auto">{pending}</div>
          <div className="cxg__pending-actions">
            <button type="button" className="cx-btn cx-btn--sm" onClick={() => setPending(null)}
                    data-testid={`${testId}-keep`}>
              {t('bots.connectors.guideDraft.keepMine')}
            </button>
            <button type="button" className="cx-btn cx-btn--sm cx-btn--primary"
                    onClick={() => { onUse(pending); setPending(null); }} data-testid={`${testId}-use`}>
              {t('bots.connectors.guideDraft.useDraft')}
            </button>
          </div>
        </div>
      )}
      {meta?.cutOff && (
        <p className="cxg__meta" data-testid={`${testId}-cut-off`}>{t('bots.connectors.guideDraft.cutOff')}</p>
      )}
      {meta && pending === null && (
        <p className="cxg__meta" data-testid={`${testId}-meta`}>
          {t('bots.connectors.guideDraft.written', { model: meta.model })}
          {meta.cost > 0 && <span dir="ltr"> · ${meta.cost < 0.01 ? meta.cost.toFixed(4) : meta.cost.toFixed(2)}</span>}
        </p>
      )}
    </div>
  );
}

function SparkGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" />
      <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
    </svg>
  );
}
